import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getUserInterestsOptions,
} from '../../api/generated/@tanstack/react-query.gen';
import {
  createUserInterest,
  deleteUserInterest,
} from '../../api/generated/sdk.gen';
import type { UserInterest } from '../../api/generated/types.gen';

// Re-export generated type
export type { UserInterest };

/**
 * Query hook for fetching a specific tutor's interests (for viewing another tutor's profile)
 */
export function useTutorInterestsQuery(userId: string | undefined) {
  return useQuery({
    ...getUserInterestsOptions({ path: { userId: userId! } }),
    select: (data) => data.interests,
    enabled: !!userId,
  });
}

/**
 * Query hook for fetching the current user's interests
 * Uses the same endpoint but with a different cache key pattern for "my" data
 */
export function useMyInterestsQuery(userId: string | undefined) {
  return useQuery({
    ...getUserInterestsOptions({ path: { userId: userId! } }),
    select: (data) => data.interests,
    enabled: !!userId,
  });
}

/**
 * Mutation hook for creating a new interest (adding an interest to profile)
 * Adapts the simple shortCode param to the generated SDK format
 */
export function useCreateInterestMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (shortCode: string) => {
      const { data } = await createUserInterest({
        body: { short_code: shortCode },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: () => {
      // Invalidate all user interest queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: [{ _id: 'getUserInterests' }] });
    },
  });
}

/**
 * Mutation hook for deleting an interest (removing an interest from profile)
 * Adapts the simple shortCode param to the generated SDK format
 */
export function useDeleteInterestMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (shortCode: string) => {
      const { data } = await deleteUserInterest({
        body: { short_code: shortCode },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: () => {
      // Invalidate all user interest queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: [{ _id: 'getUserInterests' }] });
    },
  });
}
